import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { showValidationMsg } from '@sharedModule/utils/form.utils';
import { environment } from '@env/environment';
import { Subject, takeUntil } from 'rxjs';
import { AccountDeletionService } from './services/account-deletion.service';

type DeletionStep = 'request' | 'confirm' | 'done';

@Component({
  selector: 'app-account-deletion',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './account-deletion.component.html',
  styleUrl: './account-deletion.component.scss',
})
export class AccountDeletionComponent implements OnInit, OnDestroy {
  public readonly partnerName = environment.partnerName;
  public readonly supportEmail = environment.supportEmail;
  public readonly dataRetentionYears = environment.dataRetentionYears;
  public readonly mailtoLink = `mailto:${this.supportEmail}?subject=Account Deletion Request`;

  public step: DeletionStep = 'request';
  public requestForm!: FormGroup;
  public confirmForm!: FormGroup;
  public isLoading = false;
  public errorMessage = '';
  public submittedEmail = '';

  private readonly destroy$ = new Subject<void>();
  private readonly formBuilder = inject(FormBuilder);
  private readonly accountDeletionService = inject(AccountDeletionService);

  ngOnInit(): void {
    this.initializeForms();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onRequestCode(): void {
    if (!this.requestForm.valid) {
      showValidationMsg(this.requestForm);
      return;
    }

    const email = (this.requestForm.value.email as string).trim();
    this.sendRequestCode(email, true);
  }

  public onResendCode(): void {
    if (!this.submittedEmail) {
      return;
    }
    this.sendRequestCode(this.submittedEmail, false);
  }

  public onConfirmDeletion(): void {
    if (!this.confirmForm.valid) {
      showValidationMsg(this.confirmForm);
      return;
    }

    const verificationCode = (
      this.confirmForm.value.verification_code as string
    ).trim();

    this.errorMessage = '';
    this.isLoading = true;

    this.accountDeletionService
      .confirmDeletion(this.submittedEmail, verificationCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isLoading = false;
          if (response.success) {
            this.step = 'done';
          } else {
            this.errorMessage =
              response.message || 'Unable to delete account. Please try again.';
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.errorMessage = this.extractErrorMessage(error);
        },
      });
  }

  private initializeForms(): void {
    this.requestForm = this.formBuilder.group({
      email: [
        '',
        [Validators.required, Validators.pattern(REGEX_CONSTANT.email)],
      ],
    });

    this.confirmForm = this.formBuilder.group({
      verification_code: ['', [Validators.required]],
      confirmation: [false, [Validators.requiredTrue]],
    });
  }

  private sendRequestCode(email: string, advanceStep: boolean): void {
    this.errorMessage = '';
    this.isLoading = true;

    this.accountDeletionService
      .requestCode(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isLoading = false;
          if (response.success) {
            this.submittedEmail = email;
            if (advanceStep) {
              this.confirmForm.reset({
                verification_code: '',
                confirmation: false,
              });
              this.step = 'confirm';
            }
          } else {
            this.errorMessage =
              response.message ||
              'Unable to send verification code. Please try again.';
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.errorMessage = this.extractErrorMessage(error);
        },
      });
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    return (
      error?.error?.error?.message ||
      error?.error?.message ||
      'Something went wrong. Please try again.'
    );
  }
}
